// Pure selectors over the store, so a consumer re-renders only for the slice
// it reads. The user's exclusions decide which rows are excluded: a row the
// user excluded counts as excluded here even before a re-check has told the
// server, and a row the last check answered as excluded but the user has
// included since has no answer until the next check (unchecked).
import type { CatalogImportLineResult } from '@/api/models';
import type {
    CatalogImportSnapshot,
    CatalogImportState,
    ParsedLine,
} from './catalogImportReducer';

/**
 * The Products table's tabs. A row can be under Needs attention and a match
 * tab; Excluded holds the rows the user left out, which no other tab but All
 * lists (bos-app#220).
 */
export type CatalogImportTab =
    'all' | 'needsAttention' | 'newProducts' | 'restock' | 'excluded';

/** The tabs in the order the table shows them; All first, the default. */
export const CATALOG_IMPORT_TABS: readonly CatalogImportTab[] = [
    'all',
    'needsAttention',
    'newProducts',
    'restock',
    'excluded',
];

export const isCatalogImportTab = (value: unknown): value is CatalogImportTab =>
    CATALOG_IMPORT_TABS.includes(value as CatalogImportTab);

/** How many rows each tab holds. */
export type CatalogImportTabCounts = Record<CatalogImportTab, number>;

/**
 * What the row selectors read: the preview and the user's exclusions. A
 * consumer that memoizes on these two alone can pass just them.
 */
export type CatalogImportRowsSource = Pick<
    CatalogImportState,
    'preview' | 'exclusions'
>;

/**
 * What the import gate reads: the rows' sources and whether a preview or the
 * import itself runs.
 */
export type CatalogImportGateSource = Pick<
    CatalogImportState,
    'preview' | 'exclusions' | 'previewStatus' | 'importStatus'
>;

/**
 * Why Import is disabled, the first that applies: the import is running, a
 * check is running, rows the user included since the last check have no
 * answer yet, rows need attention, or no row is ready.
 */
export type CatalogImportBlocker =
    'importing' | 'checking' | 'unchecked' | 'needsAttention' | 'nothingReady';

const isExcluded = (
    state: CatalogImportRowsSource,
    line: CatalogImportLineResult,
): boolean => state.exclusions.has(line.rowNumber);

/** Answered as excluded by the last check, included by the user since. */
const isUnchecked = (
    state: CatalogImportRowsSource,
    line: CatalogImportLineResult,
): boolean => line.status === 'excluded' && !isExcluded(state, line);

const previewLines = (
    state: Pick<CatalogImportState, 'preview'>,
): readonly CatalogImportLineResult[] => state.preview?.lines ?? [];

/**
 * The tab rule: All holds every row, excluded ones included (the table marks
 * them); Excluded holds exactly the rows the user excluded (the store's
 * exclusions, before any re-check has told the server); the other tabs leave
 * excluded rows out. Needs attention goes by the status, New products (a new
 * product or a new variant) and Restock by the match, so a row can be under
 * Needs attention and a match tab. An unchecked row has neither (the server
 * answers an excluded row with no match), so it is under All alone until the
 * next check answers it.
 */
const isInTab = (
    state: CatalogImportRowsSource,
    line: CatalogImportLineResult,
    tab: CatalogImportTab,
): boolean => {
    if (tab === 'all') {
        return true;
    }
    if (tab === 'excluded') {
        return isExcluded(state, line);
    }
    if (isExcluded(state, line)) {
        return false;
    }
    switch (tab) {
        case 'needsAttention':
            return line.status === 'needsAttention';
        case 'newProducts':
            return line.match === 'newProduct' || line.match === 'newVariant';
        case 'restock':
            return line.match === 'restock';
    }
};

/** Rows the import would write: ready and not excluded. */
export const readyCount = (state: CatalogImportRowsSource): number =>
    previewLines(state).filter(
        (line) => line.status === 'ready' && !isExcluded(state, line),
    ).length;

/** Rows that block the import: needing attention and not excluded. */
export const needsAttentionCount = (state: CatalogImportRowsSource): number =>
    previewLines(state).filter(
        (line) => line.status === 'needsAttention' && !isExcluded(state, line),
    ).length;

/**
 * Rows with no answer: the last check left them out, and the user included
 * them since. They block the import until a check answers them.
 */
export const uncheckedCount = (state: CatalogImportRowsSource): number =>
    previewLines(state).filter((line) => isUnchecked(state, line)).length;

/** An import was sent and has not answered yet. */
export const isImporting = (
    state: Pick<CatalogImportState, 'importStatus'>,
): boolean => state.importStatus === 'running';

/**
 * Why Import is disabled, or `null` when it is not: never while the import
 * or a preview runs, never while a row has no answer or a row needs
 * attention, and never with nothing to import. The rest is the import's own
 * check: the server checks every row again and refuses when one needs
 * attention (`IMPORT_LINES_INVALID`).
 */
export const importBlocker = (
    state: CatalogImportGateSource,
): CatalogImportBlocker | null => {
    if (isImporting(state)) {
        return 'importing';
    }
    if (state.previewStatus === 'running') {
        return 'checking';
    }
    if (uncheckedCount(state) > 0) {
        return 'unchecked';
    }
    if (needsAttentionCount(state) > 0) {
        return 'needsAttention';
    }
    if (readyCount(state) === 0) {
        return 'nothingReady';
    }
    return null;
};

/** Import is enabled: nothing blocks it (`importBlocker`). */
export const canImport = (state: CatalogImportGateSource): boolean =>
    importBlocker(state) === null;

/**
 * A tab's rows, in file order. All shows every row, excluded ones included
 * (the table marks them), and Excluded only those; the other tabs leave
 * excluded rows out.
 */
export const linesForTab = (
    state: CatalogImportRowsSource,
    tab: CatalogImportTab,
): CatalogImportLineResult[] =>
    previewLines(state).filter((line) => isInTab(state, line, tab));

/**
 * The needs-attention rows that are not excluded, in file order: what
 * "Exclude all that need attention" leaves out.
 */
export const needsAttentionRows = (state: CatalogImportRowsSource): number[] =>
    linesForTab(state, 'needsAttention').map((line) => line.rowNumber);

/**
 * The rows the user excluded, in file order: the Excluded tab's rows, and
 * what "Include all back" takes back.
 */
export const excludedRows = (state: CatalogImportRowsSource): number[] =>
    linesForTab(state, 'excluded').map((line) => line.rowNumber);

/**
 * Each tab's row count, by the same rule as `linesForTab`. Right after a
 * check these are the preview's totals (`totals.lines`: All = ready + needs
 * attention + excluded, New products = new product + new variant); a row
 * the user excludes since leaves its tabs at once, as it leaves their rows.
 */
export const tabCounts = (
    state: CatalogImportRowsSource,
): CatalogImportTabCounts => {
    const counts: CatalogImportTabCounts = {
        all: 0,
        needsAttention: 0,
        newProducts: 0,
        restock: 0,
        excluded: 0,
    };
    for (const line of previewLines(state)) {
        for (const tab of CATALOG_IMPORT_TABS) {
            if (isInTab(state, line, tab)) {
                counts[tab] += 1;
            }
        }
    }
    return counts;
};

/**
 * The other rows of a row's product group: the preview's `groupRows` once
 * there is one, else the parsed rows with the same SKU. A row without a
 * group, or an unknown row, has none.
 */
export const groupSiblings = (
    state: CatalogImportState,
    rowNumber: number,
): number[] => {
    const resolved = previewLines(state).find(
        (line) => line.rowNumber === rowNumber,
    );
    if (resolved) {
        return resolved.groupRows.filter((row) => row !== rowNumber);
    }

    const sku = state.lines
        .find((line) => line.rowNumber === rowNumber)
        ?.sku?.trim();
    if (!sku) {
        return [];
    }
    return state.lines
        .filter(
            (line) => line.rowNumber !== rowNumber && line.sku?.trim() === sku,
        )
        .map((line) => line.rowNumber);
};

/**
 * Work that would be lost by leaving: a file has been parsed and nothing
 * has been imported from it yet. An import on its way is still dirty: until
 * it answers, nothing is known to be saved. Once the server refused the
 * branch (`scopeRefused`) nothing can be imported from the file, so there is
 * nothing to lose.
 */
export const isDirty = (state: CatalogImportState): boolean =>
    state.file !== null && state.record === null && !state.scopeRefused;

/** Whether a bulk action can be undone: one was done and not undone yet. */
export const canUndo = (
    state: Pick<CatalogImportState, 'undoStack'>,
): boolean => state.undoStack.length > 0;

/** The bulk action `undo` would undo, the most recent; `null` with none. */
export const latestBulkAction = (
    state: Pick<CatalogImportState, 'undoStack'>,
): CatalogImportSnapshot | null => state.undoStack.at(-1) ?? null;

const sameLine = (a: ParsedLine | undefined, b: ParsedLine | undefined) =>
    a === b || JSON.stringify(a) === JSON.stringify(b);

/**
 * How many rows `undo` would restore: those whose line or exclusion differs
 * from the latest snapshot, a drawer edit made since the action included
 * (the undo message's count). `0` with nothing to undo.
 */
export const undoRestoreCount = (
    state: Pick<CatalogImportState, 'undoStack' | 'lines' | 'exclusions'>,
): number => {
    const snapshot = state.undoStack.at(-1);
    if (!snapshot) {
        return 0;
    }
    const before = new Map(
        snapshot.lines.map((line) => [line.rowNumber, line]),
    );
    const rows = new Set<number>();
    for (const line of state.lines) {
        if (!sameLine(before.get(line.rowNumber), line)) {
            rows.add(line.rowNumber);
        }
    }
    for (const row of new Set([...snapshot.exclusions, ...state.exclusions])) {
        if (snapshot.exclusions.has(row) !== state.exclusions.has(row)) {
            rows.add(row);
        }
    }
    return rows.size;
};
